package com.islamicmedia.shared.domain.model

import kotlinx.serialization.Serializable

@Serializable
data class QuranReference(
    val english: String,
    val arabic: String,
    val reference: String
)

@Serializable
data class HadithReference(
    val english: String,
    val arabic: String,
    val reference: String
)

@Serializable
data class TldrPoint(
    val text: String,
    val severity: String = "medium",  // "high" | "medium" | "positive"
    val category: String? = null
)

@Serializable
data class Concern(
    val category: String,
    val severity: String,
    val confidence: String,
    val description: String,
    val islamicPerspective: String? = null,
    val developmentalPerspective: String? = null,
    val quranReference: QuranReference? = null,
    val hadithReference: HadithReference? = null,
    val evidence: String? = null,
    /** The specific moments behind this concern (scenes, episodes, recurring elements), listed by
     *  Phase 1 scoring. Available the moment the verdict lands, so a card has something concrete to
     *  show while its write-up is still being produced. Empty for analyses made before it existed. */
    val instances: List<String> = emptyList(),
    /** "done" | "failed" | "pending" | null. Phase 2 enrichment status — UI renders failed cards as
     *  grey + retry icon, and pending cards with their Phase 1 text plus a "being written" body. */
    val enrichmentStatus: String? = null
)

@Serializable
data class AnalysisResult(
    val score: Int,
    val recommendation: String,
    val summary: String? = null,
    val concerns: List<Concern> = emptyList(),
    val ageGuidance: String? = null,
    val positiveAspects: List<String> = emptyList(),
    val additionalNotes: String? = null,
    val islamicScore: Int? = null,
    val developmentalScore: Int? = null,
    val quickCheck: Boolean = false,
    /** Set on quick-check results when a full analysis already exists in cache. */
    val fullAnalysisAvailable: Boolean = false,
    /** Poster/cover image resolved server-side (TMDB/RAWG/YouTube/Books). Null when none found. */
    val coverUrl: String? = null,
    /** Content version = epoch-millis of the title's last propagation-worthy edit (concern/score
     *  edit or cover change); 0 when never edited, null on results that predate versioning. Used to
     *  detect when a stored analysis is stale and re-fetch it so admin edits reach existing users. */
    val version: Long? = null,
    /** Highlights reel: specific examples from the analysis (exact language used, key scenes, etc).
     *  Null for analyses that predate this field. Stored in media_phase1 (criteria-independent). */
    val tldr: List<TldrPoint>? = null,
    /** Book author, resolved server-side from media_content.author (like [coverUrl]) so it stays
     *  in sync with admin corrections. Null for non-books or unresolved authors. */
    val author: String? = null,
    /** True ONLY on the partial result streamed in `phase1_complete` for a FULL analysis: the verdict
     *  (score, severities, positives) is FINAL — Phase 2 contributes nothing to the score — but the
     *  prose, references and age guidance are still arriving. Distinguishes "Phase 1 of a Full run"
     *  from a genuine Quick check, which carries the same concern shape but must offer the quick→full
     *  upgrade. Never true on a persisted, cached or history result. */
    val analysisPending: Boolean = false
)
